import { useState, useEffect, useRef } from 'react';

export const useWebSocket = (orgId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  
  // Exponential backoff configuration
  const reconnectAttempts = useRef(0);
  const maxReconnectDelay = 30000; // 30 seconds max delay
  const baseDelay = 1000; // 1 second initial delay

  const connect = () => {
    if (!orgId) return;

    // The shop is taken from the token now, not from `?orgId=` — the server
    // stopped believing an id it was handed in the address.
    //
    // The token is read on every attempt rather than captured once. An access
    // token lasts an hour and a till stays open all day, so a reconnect that
    // reused the token from the original connection would start being refused
    // the moment it expired, and the screen would quietly stop updating.
    const token = localStorage.getItem('org.accessToken');
    if (!token) {
      scheduleReconnect();
      return;
    }

    // Use wss:// if https, ws:// if http
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;

    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log('[WebSocket] Connected securely');
      setIsConnected(true);
      reconnectAttempts.current = 0; // Reset attempts on successful connection
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);
      } catch (e) {
        console.error('[WebSocket] Failed to parse message', e);
      }
    };

    ws.current.onclose = () => {
      console.log('[WebSocket] Disconnected');
      setIsConnected(false);
      scheduleReconnect();
    };

    ws.current.onerror = (error) => {
      console.error('[WebSocket] Error occurred', error);
      // onclose will trigger and schedule reconnect
    };
  };

  const scheduleReconnect = () => {
    // Calculate next delay using exponential backoff: delay = base * 2^attempts
    let delay = baseDelay * Math.pow(2, reconnectAttempts.current);
    if (delay > maxReconnectDelay) {
      delay = maxReconnectDelay;
    }

    console.log(`[WebSocket] Reconnecting in ${delay}ms...`);
    reconnectTimeout.current = setTimeout(() => {
      reconnectAttempts.current++;
      connect();
    }, delay);
  };

  useEffect(() => {
    connect();

    return () => {
      // Cleanup on unmount
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [orgId]);

  return { isConnected, lastMessage };
};
